import React, { useEffect, useState } from 'react';
import { Button, Card, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api_url } from '../../constant/constant';

import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

const COLORS = ['#34d399', '#f87171'];

const PhishingCampaignSummary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedDepartment, setSelectedDepartment] = useState([]);
  const [selectedPhishing, setSelectedPhishing] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignInfo, setCampaignInfo] = useState({});
  const [pieStats, setPieStats] = useState({});
  const [dailyActivity, setDailyActivity] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await fetch(`${api_url}/admin/main/departments`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();
      if (result && result.data) {
        setDepartments(result.data);
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };

  const fetchPhishing = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await fetch(`${api_url}/phishing-campaigns`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();
      if (result && result.data && Array.isArray(result.data.data)) {
        setCampaigns(result.data.data);
      } else {
        setCampaigns([]);
      }
    } catch (err) {
      console.error('Error fetching phishing campaigns:', err);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('userToken');
      const response = await fetch(
        `${api_url}/report/phishing/campaign-summary?campaign_id=${selectedPhishing}&department_id=${selectedDepartment}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if ([401, 403, 429].includes(response.status)) {
        localStorage.removeItem('userToken');
        navigate('/login');
        return;
      }

      const result = await response.json();

      if (result.status === 'success') {
        const data = result.data;
        setCampaignInfo(data.campaign_summary || {});
        setPieStats(data.pie_charts || {});
        setDailyActivity(data.daily_activity?.data || {});
      } else {
        throw new Error(result.message || 'Failed to fetch campaign data.');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message || 'An error occurred while fetching data.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPhishing();
    fetchDepartments();
  }, []);

  // if (isLoading) return <div className="text-center py-5">{t('Loading')}...</div>;
  // if (error) return <div className="text-danger text-center py-5">{error}</div>;

  const getPieData = (obj) => {
    if (!obj) return [];
    return Object.entries(obj).map(([key, value]) => ({
      name: key.replace('_', ' '),
      value,
    }));
  };

  return (
    <div className='container py-4'>
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <div className='pageTitle'>{t('PhishingCampaignSummary')}</div>
        <Button variant='primary' onClick={() => navigate(-1)}>
          {t('Back')}
        </Button>
      </div>

      <div className='mb-4 p-3 card'>
        <div
          className='p-3'
          style={{
            backgroundColor: '#f0f4f8',
            borderRadius: '16px',
          }}
        >
          <div className='row g-3 align-items-center'>
            {/* Department Dropdown */}
            {/* Campaign Dropdown */}
            <div className='col-md-4'>
              <label className='form-label'>{t('SelectPhishing')}</label>
              <select
                className='form-select'
                value={selectedPhishing}
                onChange={(e) => setSelectedPhishing(e.target.value)}
              >
                <option value=''>{t('SelectPhishing')}</option>
                {campaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name_en}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-md-4'>
              <label className='form-label'>{t('SelectDepartments')}</label>
              <select
                className='form-select'
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
              >
                <option value=''>{t('SelectDepartment')}</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Filter Button */}
            <div>
              <Button
                className='btn btn-success '
                onClick={() => {
                  fetchData();
                }}
              >
                {t('ApplyFilters')}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {isLoading && <div className='text-center py-5'>{t('Loading')}...</div>}
      {error && <div className='text-danger text-center py-5'>{error}</div>}
      {/* Campaign Info Cards */}
      <div className='row g-3 mb-4'>
        <div className='col-md-3'>
          <Card className='p-3 text-center'>
            <h4 className='fw-bold'>{campaignInfo?.name || '-'}</h4>
            <small className='text-muted'>{t('CampaignTitle')}</small>
          </Card>
        </div>
        <div className='col-md-3'>
          <Card className='p-3 text-center'>
            <h4 className='fw-bold'>{campaignInfo?.enrolled_users_count || 0}</h4>
            <small className='text-muted'>{t('EnrolledUsers')}</small>
          </Card>
        </div>
        <div className='col-md-3'>
          <Card className='p-3 text-center'>
            <h4 className='fw-bold'>{campaignInfo?.status || '-'}</h4>
            <small className='text-muted'>{t('Status')}</small>
          </Card>
        </div>
        <div className='col-md-3'>
          <Card className='p-3 text-center'>
            <h4 className='mb-1 fw-bold'>{campaignInfo?.start_date || '-'}</h4>
            <small className='text-muted'>{t('CampaignStartDate')}</small>
          </Card>
        </div>
        <div className='col-md-3'>
          <Card className='p-3 text-center'>
            <h4 className='mb-1 fw-bold'>{campaignInfo?.end_date || '-'}</h4>
            <small className='text-muted'>{t('CampaignEndDate')}</small>
          </Card>
        </div>
      </div>

      {/* Pie Charts Section */}
      <div className='row g-4 mb-4'>
        <div className='col-md-4'>
          <Card
            className={`p-3 text-center ${document.documentElement.getAttribute('dir') === 'rtl' ? 'text-end' : 'text-start'}`}
          >
            <h6 className='mb-3'>{t('EmailsOpened')}</h6>
            {/* <ResponsiveContainer width="100%" height={250}>
        <PieChart>
          <Pie
            data={getPieData(pieStats.emails_opened)}
            dataKey="value"
            nameKey="name"
            outerRadius={80}
            label
            
          >
            {getPieData(pieStats.emails_opened).map((_, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer> */}

            <ResponsiveContainer width='100%' height={250}>
              <PieChart>
                <Pie
                  data={getPieData(pieStats.emails_opened)}
                  dataKey='value'
                  nameKey='name'
                  outerRadius={80}
                  labelLine={({ cx, cy, midAngle, outerRadius, index }) => {
                    const r = outerRadius + 13;
                    const RADIAN = Math.PI / 180;
                    const x1 = cx + outerRadius * Math.cos(-midAngle * RADIAN);
                    const y1 = cy + outerRadius * Math.sin(-midAngle * RADIAN);
                    const x2 = cx + r * Math.cos(-midAngle * RADIAN);
                    const y2 = cy + r * Math.sin(-midAngle * RADIAN);
                    return (
                      <line
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke={COLORS[index % COLORS.length]}
                      />
                    );
                  }}
                  label={({ cx, cy, midAngle, outerRadius, value, index }) => {
                    const r = outerRadius + 16;
                    const RADIAN = Math.PI / 180;
                    const x = cx + r * Math.cos(-midAngle * RADIAN);
                    const y = cy + r * Math.sin(-midAngle * RADIAN);
                    return (
                      <text
                        x={x}
                        y={y}
                        fill={COLORS[index % COLORS.length]}
                        textAnchor={x > cx ? 'start' : 'end'}
                        dominantBaseline='central'
                        style={{ fontSize: 14, direction: 'ltr' }}
                      >
                        {value}
                      </text>
                    );
                  }}
                >
                  {getPieData(pieStats.emails_opened).map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </div>

        <div className='col-md-4'>
          <Card
            className={`p-3 text-center ${document.documentElement.getAttribute('dir') === 'rtl' ? 'text-end' : 'text-start'}`}
          >
            <h6 className='mb-3'>{t('LinksClicked')}</h6>
            {/* <ResponsiveContainer width="100%" height={250}>
        <PieChart>
          <Pie
            data={getPieData(pieStats.links_clicked)}
            dataKey="value"
            nameKey="name"
            outerRadius={80}
            label
          >
            {getPieData(pieStats.links_clicked).map((_, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer> */}

            <ResponsiveContainer width='100%' height={250}>
              <PieChart>
                <Pie
                  data={getPieData(pieStats.links_clicked)}
                  dataKey='value'
                  nameKey='name'
                  outerRadius={80}
                  labelLine={({ cx, cy, midAngle, outerRadius, index }) => {
                    const r = outerRadius + 13;
                    const RADIAN = Math.PI / 180;
                    const x1 = cx + outerRadius * Math.cos(-midAngle * RADIAN);
                    const y1 = cy + outerRadius * Math.sin(-midAngle * RADIAN);
                    const x2 = cx + r * Math.cos(-midAngle * RADIAN);
                    const y2 = cy + r * Math.sin(-midAngle * RADIAN);
                    return (
                      <line
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke={COLORS[index % COLORS.length]}
                      />
                    );
                  }}
                  label={({ cx, cy, midAngle, outerRadius, value, index }) => {
                    const r = outerRadius + 16;
                    const RADIAN = Math.PI / 180;
                    const x = cx + r * Math.cos(-midAngle * RADIAN);
                    const y = cy + r * Math.sin(-midAngle * RADIAN);
                    return (
                      <text
                        x={x}
                        y={y}
                        fill={COLORS[index % COLORS.length]}
                        textAnchor={x > cx ? 'start' : 'end'}
                        dominantBaseline='central'
                        style={{ fontSize: 14, direction: 'ltr' }}
                      >
                        {value}
                      </text>
                    );
                  }}
                >
                  {getPieData(pieStats.links_clicked).map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </div>

        <div className='col-md-4'>
          <Card
            className={`p-3 text-center ${document.documentElement.getAttribute('dir') === 'rtl' ? 'text-end' : 'text-start'}`}
          >
            <h6 className='mb-3'>{t('FormsSubmitted')}</h6>
            <ResponsiveContainer width='100%' height={250}>
              <PieChart>
                <Pie
                  data={getPieData(pieStats.forms_submitted)}
                  dataKey='value'
                  nameKey='name'
                  outerRadius={80}
                  labelLine={({ cx, cy, midAngle, outerRadius, index }) => {
                    const r = outerRadius + 13;
                    const RADIAN = Math.PI / 180;
                    const x1 = cx + outerRadius * Math.cos(-midAngle * RADIAN);
                    const y1 = cy + outerRadius * Math.sin(-midAngle * RADIAN);
                    const x2 = cx + r * Math.cos(-midAngle * RADIAN);
                    const y2 = cy + r * Math.sin(-midAngle * RADIAN);
                    return (
                      <line
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke={COLORS[index % COLORS.length]}
                      />
                    );
                  }}
                  label={({ cx, cy, midAngle, outerRadius, value, index }) => {
                    const r = outerRadius + 16;
                    const RADIAN = Math.PI / 180;
                    const x = cx + r * Math.cos(-midAngle * RADIAN);
                    const y = cy + r * Math.sin(-midAngle * RADIAN);
                    return (
                      <text
                        x={x}
                        y={y}
                        fill={COLORS[index % COLORS.length]}
                        textAnchor={x > cx ? 'start' : 'end'}
                        dominantBaseline='central'
                        style={{ fontSize: 14, direction: 'ltr' }}
                      >
                        {value}
                      </text>
                    );
                  }}
                >
                  {getPieData(pieStats.forms_submitted).map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </div>
      </div>

      {/* Daily Activity Table */}
      <div className='mb-4'>
        <Card className='p-3'>
          <h5 className='mb-3'>{t('DailyActivity')}</h5>
          <Table responsive bordered striped>
            <thead>
              <tr>
                <th>{t('Date')}</th>
                <th>{t('emails_opened')}</th>
                <th>{t('links_clicked')}</th>
                <th>{t('forms_submitted')}</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(dailyActivity).map(([date, stats]) => (
                <tr key={date}>
                  <td>{date}</td>
                  <td>{stats.emails_opened}</td>
                  <td>{stats.links_clicked}</td>
                  <td>{stats.forms_submitted}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      </div>
    </div>
  );
};

export default PhishingCampaignSummary;
